import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { api, unwrapObject } from "@/lib/api";
import { SectionCard, TargetBar, fmtMoney, fmtNum } from "@/components/reports/ReportWidgets";

interface TargetRow {
  user_id: string;
  name: string;
  role_code: string;
  role_name: string;
  sales_head: string | null;
  target_amount: number;
  target_conversions: number;
  won_amount: number;
  leads_converted: number;
  achieved_pct: number | null;
  can_edit: boolean;
}

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export function TargetsPage() {
  const { hasPermission, getScope } = useAuth();
  const { success, error } = useToast();
  const [period, setPeriod] = useState(currentPeriod());
  const [rows, setRows] = useState<TargetRow[]>([]);
  const [edits, setEdits] = useState<Record<string, { target_amount: string; target_conversions: string }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canSet = hasPermission("target.set");
  const isSuperAdmin = getScope("report") === "all";

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/Target/List?period=${period}`);
      const data = unwrapObject<{ rows: TargetRow[] }>(res);
      setRows(data.rows || []);
      setEdits({});
    } catch (err: any) {
      error("Targets", err.message || "Failed to load targets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [period]);

  const dirtyIds = Object.keys(edits);
  const valueOf = (r: TargetRow, field: "target_amount" | "target_conversions") =>
    edits[r.user_id]?.[field] ?? String(r[field] || "");

  const setField = (r: TargetRow, field: "target_amount" | "target_conversions", value: string) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setEdits((prev) => ({
      ...prev,
      [r.user_id]: {
        target_amount: prev[r.user_id]?.target_amount ?? String(r.target_amount || ""),
        target_conversions: prev[r.user_id]?.target_conversions ?? String(r.target_conversions || ""),
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/api/Target/Save", {
        period,
        items: dirtyIds.map((id) => ({
          user_id: id,
          target_amount: Number(edits[id]?.target_amount) || 0,
          target_conversions: Math.round(Number(edits[id]?.target_conversions) || 0),
        })),
      });
      success("Targets saved", `Saved ${dirtyIds.length} target${dirtyIds.length === 1 ? "" : "s"} for ${period}`);
      await load();
    } catch (err: any) {
      error("Save failed", err.message || "Could not save targets");
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    const heads = rows.filter((r) => r.role_code === "SALES_HEAD");
    const execs = rows.filter((r) => r.role_code === "SALES_EXEC");
    return [
      { title: "Sales Heads", description: "Team target: compared with the whole team's revenue", rows: heads },
      { title: "Sales Executives", description: "Compared with each executive's own revenue", rows: execs },
    ].filter((g) => g.rows.length > 0);
  }, [rows]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">Sales Targets</h2>
          <p className="text-sm text-muted-foreground">
            {canSet
              ? isSuperAdmin
                ? "Set monthly revenue and conversion targets for Sales Heads and Sales Executives."
                : "Set monthly targets for the Sales Executives in your team."
              : "Your monthly target and progress."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="target-period" className="text-xs text-muted-foreground">
            Month
          </label>
          <Input id="target-period" type="month" className="h-8 w-[150px] text-xs" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)} />
          {canSet && (
            <Button size="sm" onClick={handleSave} disabled={saving || dirtyIds.length === 0}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              Save{dirtyIds.length ? ` (${dirtyIds.length})` : ""}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : groups.length === 0 ? (
        <SectionCard title="No targets">
          <p className="text-xs text-muted-foreground">There is nobody you can set targets for yet.</p>
        </SectionCard>
      ) : (
        groups.map((g) => (
          <SectionCard key={g.title} title={g.title} description={g.description}>
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="py-2 px-2 text-left font-semibold">Name</th>
                    {g.title === "Sales Executives" && <th className="py-2 px-2 text-left font-semibold">Sales Head</th>}
                    <th className="py-2 px-2 text-right font-semibold">Revenue target (₹)</th>
                    <th className="py-2 px-2 text-right font-semibold">Conversion target</th>
                    <th className="py-2 px-2 text-right font-semibold">Converted</th>
                    <th className="py-2 px-2 text-left font-semibold min-w-[180px]">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.user_id} className="border-b border-border/60 last:border-0">
                      <td className="py-2 px-2 font-medium text-foreground whitespace-nowrap">{r.name}</td>
                      {g.title === "Sales Executives" && <td className="py-2 px-2 text-muted-foreground whitespace-nowrap">{r.sales_head || "—"}</td>}
                      <td className="py-2 px-2 text-right">
                        {r.can_edit ? (
                          <Input
                            inputMode="decimal"
                            aria-label={`Revenue target for ${r.name}`}
                            className="h-8 w-[130px] ml-auto text-right text-xs tabular-nums"
                            value={valueOf(r, "target_amount")}
                            placeholder="0"
                            onChange={(e) => setField(r, "target_amount", e.target.value)}
                          />
                        ) : (
                          <span className="tabular-nums">{r.target_amount ? fmtMoney(r.target_amount, false) : "—"}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {r.can_edit ? (
                          <Input
                            inputMode="numeric"
                            aria-label={`Conversion target for ${r.name}`}
                            className="h-8 w-[90px] ml-auto text-right text-xs tabular-nums"
                            value={valueOf(r, "target_conversions")}
                            placeholder="0"
                            onChange={(e) => setField(r, "target_conversions", e.target.value.replace(/\D/g, ""))}
                          />
                        ) : (
                          <span className="tabular-nums">{r.target_conversions || "—"}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">
                        {fmtNum(r.leads_converted)}
                        {r.target_conversions ? <span className="text-muted-foreground"> / {r.target_conversions}</span> : null}
                      </td>
                      <td className="py-2 px-2">
                        <TargetBar achieved={r.won_amount} target={Number(valueOf(r, "target_amount")) || 0} compact />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        ))
      )}
    </div>
  );
}
